/**
 * FromItemRow — a single FROM-segment row (leading item text/derived subquery +
 * its × remove control), extracted from FromSection so the host stays under the
 * 200-line budget.
 *
 * Owns the per-row "remove the only data source" confirm flow. The × handler
 * dispatches `removeTable` with the live QueryStructure so `removeTable`'s
 * internal only-source detection can fire; when it removes the ONLY FROM item it
 * awaits `confirmIfOnlySource`, which stores a Promise resolver in local state
 * and opens the shadcn <ConfirmDestructiveDialog>. Confirm resolves true (and
 * fires `feature:vqe_destructive_confirm_apply`); cancel resolves false (and
 * fires `feature:vqe_destructive_confirm_cancel`). This replaces the previous
 * native window.confirm — no VQE file calls window.confirm.
 *
 * COM-21/COM-22: a derived-table row threads its statement-level `editContext`
 * into `<SubquerySection>` (gated on the same `editReady` this row already
 * uses for remove/edit) so the expanded body is inline-editable, not just a
 * drill-to-focus preview — mirrors `CteCard`'s body-edit threading. The ×
 * remove control's aria-label also names what it deletes ("Remove derived
 * table" vs "Remove table").
 */
import { useState } from 'react';

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { removeTable } from '../../edit/edits/removeTable';
import { ConfirmDestructiveDialog } from '../../ux/ConfirmDestructiveDialog';
import { buildDrillLabel, extractRelationIdentifier } from '../../ux/drillLabel';
import { vqeCopy } from '../../ux/vqeCopy';
import { RemoveButton } from '../common/RemoveButton';
import type { SubqueryRef } from './extractSubqueries';
import { FromCteRefBadge } from './FromCteRefBadge';
import type { CteNameEntry } from './fromCteRef';
import { matchCteRefByName } from './fromCteRef';
import type { SubqueryWiring } from './SelectRenderer';
import { resolveFromStructure } from './fromSectionHelpers';
import { FromTableChooser } from './FromTableChooser';
import { SubquerySection } from './SubquerySection';

interface PendingRemove {
  readonly resolve: (proceed: boolean) => void;
}

export interface FromItemRowProps {
  readonly index: number;
  readonly leadingText: string;
  readonly bufferText: string;
  readonly options: ParseOptions;
  readonly editReady: boolean;
  readonly editContext?: VqeEditContext;
  readonly derived?: SubqueryRef;
  readonly subqueryCtx?: SubqueryWiring;
  /**
   * CTE names declared by the enclosing WITH clause (threaded from
   * `SelectRenderer` via `FromSection` — see `fromCteRef.ts`). Used only by
   * the CTE-ref badge's NAME-MATCH fallback when no edit-ready
   * `QueryStructure` is reachable.
   */
  readonly cteNames?: readonly CteNameEntry[];
}

export function FromItemRow({
  index,
  leadingText,
  bufferText,
  options,
  editReady,
  editContext,
  derived,
  subqueryCtx,
  cteNames,
}: FromItemRowProps) {
  const { track } = useAnalytics();
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(null);

  const isDerived = derived !== undefined && subqueryCtx !== undefined;

  // Task cte-context (COM-05/06/08) — CTE-reference discriminator. Prefer the
  // resolver's authoritative signal (a resolved FROM item's `target.kind ===
  // 'cte-ref'`, mirroring compact/compactDegradeDetail.ts's `formatTargetName`)
  // whenever a live QueryStructure is reachable (edit-ready); otherwise fall
  // back to case-folded name-matching against the threaded CTE-name set. A
  // derived (subquery) FROM item is never a CTE reference — skip both signals.
  // Perf: only resolve the outer structure for a NON-derived item — for a
  // derived (subquery) FROM item the result is provably discarded below
  // (`cteMatch = isDerived ? null : ...`, `isRelation` is only read in the
  // non-derived render branch), and `resolveFromStructure` is a non-memoized
  // whole-statement structural re-derivation on a hot render path
  // (Docs/QueryEditor_CpuIssue.md) — gating avoids paying for a thrown-away resolve.
  const structure = !isDerived && editReady && editContext ? resolveFromStructure(editContext) : undefined;
  const target = structure?.fromItems?.[index]?.target;
  const isRelation = target?.kind === 'relation';
  const cteMatch = isDerived
    ? null
    : structure
      ? target?.kind === 'cte-ref'
        ? { name: target.cte.name.name }
        : null
      : matchCteRefByName(leadingText, cteNames ?? [], options);

  const onRemove =
    editReady && editContext
      ? () => {
          // Re-entry guard: a second click before the dialog paints would
          // overwrite the resolver and leak the first Promise unresolved.
          if (pendingRemove) return;
          void removeTable(index, toCommitCtx(editContext), {
            structure: resolveFromStructure(editContext),
            confirmIfOnlySource: () =>
              new Promise<boolean>((resolve) => setPendingRemove({ resolve })),
          });
        }
      : undefined;

  const settle = (proceed: boolean): void => {
    const resolver = pendingRemove?.resolve;
    setPendingRemove(null);
    if (resolver) resolver(proceed);
  };

  const onConfirm = (): void => {
    track('feature:vqe_destructive_confirm_apply', { kind: 'only-from-source' });
    settle(true);
  };
  const onCancel = (): void => {
    track('feature:vqe_destructive_confirm_cancel', { kind: 'only-from-source' });
    settle(false);
  };

  const copy = vqeCopy.confirm.removeOnlyFromSource;

  // TODO(vqe-copy): consolidate into vqeCopy once the §4b-ii-fix lane's
  // vqeCopy edits land. COM-21/COM-22: "the large floating × doesn't say what
  // it deletes" — a derived-table row's × now says so explicitly.
  const removeLabel = isDerived ? 'Remove derived table' : 'Remove table';

  return (
    <div className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1">
      {isDerived && derived && subqueryCtx ? (
        <SubquerySection
          bufferText={bufferText}
          bodyStart={derived.bodyStart}
          bodyEnd={derived.bodyEnd}
          bodyKind={derived.bodyKind}
          options={options}
          parentStatementId={subqueryCtx.statementId}
          drillTabId={subqueryCtx.drillTabId}
          drillLabel={buildDrillLabel({
            kind: 'from',
            identifier: extractRelationIdentifier(leadingText),
            positionalIndex: index,
          })}
          outerAliases={subqueryCtx.outerAliases}
          mode="block"
          testIdPrefix={`vqe-from-item-${index}-sub`}
          onEditAsSql={subqueryCtx.onEditAsSql}
          // COM-21/COM-22: thread the statement-level editContext into the
          // block-mode derived-table body, gated on the SAME `editReady`
          // signal this row already uses for its own remove/edit affordances
          // — mirrors CteCard's body-edit threading. SubquerySection derives
          // its own child editContext (deriveSubqueryEditContext) from this.
          editContext={editReady && editContext ? editContext : undefined}
        />
      ) : (() => {
          // Render a clickable table-chooser for plain relation items when
          // edit is ready, otherwise fall back to the read-only span.
          if (editReady && editContext && isRelation && structure) {
            return (
              <FromTableChooser
                index={index}
                leadingText={leadingText}
                editContext={editContext}
                structure={structure}
              />
            );
          }
          return (
            <span
              className="flex-1 truncate font-mono text-xs"
              data-testid={`vqe-from-item-text-${index}`}
            >
              {leadingText}
            </span>
          );
        })()}
      {cteMatch ? (
        <FromCteRefBadge
          cteName={cteMatch.name}
          options={options}
          testId={`vqe-from-cte-ref-badge-${index}`}
        />
      ) : null}
      <RemoveButton
        testId={`vqe-from-remove-${index}`}
        ariaLabel={removeLabel}
        disabled={!editReady}
        tooltip={editReady ? removeLabel : undefined}
        onClick={onRemove}
      />
      <ConfirmDestructiveDialog
        open={!!pendingRemove}
        testId="vqe-confirm-remove-only-source"
        title={copy.title}
        description={copy.body}
        confirmLabel={copy.apply}
        cancelLabel={copy.cancel}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    </div>
  );
}
